import React from 'react';
import classnames from 'classnames';
import { getIntl } from 'umi';
import { cloneDeep, isFunction } from 'lodash-es';
import { Modal, Button, Checkbox, CheckChangeContext } from 'tea-component';
import ColumnAligner from '@intelligence/v3/components/TableComponents/ColumnAligner';

import type { TableColumn } from 'tea-component';
import type { IColumnBaseOption } from './utils';

import './checker.scss';

// Checker Column构建器自定义配置
interface ICheckerColumnBuilderOptionCore<T = IRecord> {
  // 列单元格的className
  className?: string;
  // 是否禁用
  disabled?: boolean;
  // 勾选数量上限，默认为10
  maxCheckLimit?: number;
  // 获取record的勾选的value
  getRecordCheckOptionValue: string | ((cvm: T) => string);
  // 获取是否全选状态
  getIsAllChecked?: (value?: string[], values?: string[] | null) => boolean;
  // 全部勾选项（并不是页面看得到的全部勾选项，仅应用于全选、取消全选、判定是否全选这三个场景）
  values?: string[] | null;
  // 勾选的值列表
  value: string[];
  // 勾选的回调
  onChange?: (payload: string[], context: CheckChangeContext) => void;
}

type ICheckerColumnBuilderOption<T> = Partial<TableColumn<T>> & IColumnBaseOption & ICheckerColumnBuilderOptionCore<T>;

/**
 * 判断是否全选（即游戏头部的Checkbox是否勾选）
 * @param value 当前勾选的值列表
 * @param values 勾选项value列表
 * @return 是否全选
 */
const getIsAllCheckedDefault = (value?: string[], values?: string[] | null) => {
  if (!Array.isArray(values) || !Array.isArray(value)) return false;
  if (values.length !== value.length) return false;
  return value.length > 0 && values.every((item) => value.includes(item));
};

/**
 * Checker Column构建器
 * @param checkerColumnBuilderOption checkerColumn构建器配置
 * @param checkerColumnBuilderOption.className 列单元格的className
 */
const CheckerColumnBuilder = <T extends IRecord = IRecord>(
  checkerColumnBuilderOption: ICheckerColumnBuilderOption<T>,
): TableColumn<T> => {
  const {
    key = 'select',
    className,
    align = 'left',
    width = 36,
    disabled = false,
    maxCheckLimit = 10,
    getRecordCheckOptionValue: getRecordCheckOptionValueProps,
    getIsAllChecked = getIsAllCheckedDefault,
    values,
    value,
    onChange,
    ...restOption
  } = checkerColumnBuilderOption || {};

  const { formatMessage } = getIntl();

  // 是否全选（表头的Checkbox）
  const isAllChecked = getIsAllChecked(value, values);
  // 是否半选（表头的Checkbox）
  const isAllIndeterminate = !isAllChecked && Array.isArray(value) && value.length > 0;

  // 获取数据的勾选value
  const getRecordCheckOptionValue = isFunction(getRecordCheckOptionValueProps)
    ? getRecordCheckOptionValueProps
    : (cvm: T) => cvm?.[getRecordCheckOptionValueProps];

  const handleChange = (payload: string[], context: CheckChangeContext) => {
    const sortedPayload = cloneDeep(payload);
    sortedPayload.sort((a, b) => {
      const aIndex = values?.indexOf(a) ?? -1;
      const bIndex = values?.indexOf(b) ?? -1;
      return aIndex - bIndex;
    });
    onChange?.(sortedPayload, context);
  };

  return {
    ...restOption,
    key,
    align,
    width,
    header: (
      <ColumnAligner align={align} width="100%">
        <div className={classnames('dbi-common-column-checker', className)}>
          <Checkbox
            className="dbi-common-column-checker__checker"
            display="block"
            disabled={disabled}
            value={isAllChecked}
            indeterminate={isAllIndeterminate}
            // 全选、取消全选
            onChange={(payload, context) => {
              const newValue = !payload ? [] : values?.slice(0, maxCheckLimit) || [];
              handleChange(newValue, context);
            }}
          />
        </div>
      </ColumnAligner>
    ),
    render: (cvm) => {
      // 当前数据关联的Checkbox的value
      const cvmValue = getRecordCheckOptionValue(cvm);
      // 当前数据关联的Checkbox的value的索引
      const cvmValueIndex = value?.indexOf(cvmValue) ?? -1;
      // 当前数据是否勾选
      const cvmChecked = cvmValueIndex !== -1;
      return (
        <ColumnAligner align={align} width="100%">
          <div className={classnames('dbi-common-column-checker', className)}>
            <Checkbox
              className={classnames(
                'dbi-common-column-checker__checker',
                // Checkbox的颜色差异通过这个class类名实现
                `dbi-common-column-checker__checker-${cvmValueIndex + 1}`,
              )}
              display="block"
              disabled={disabled}
              value={cvmChecked}
              // 勾选、取消勾选
              onChange={(payload, context) => {
                // 勾选
                if (payload && !cvmChecked) {
                  if (value?.length >= maxCheckLimit) {
                    Modal.error({
                      size: 'm',
                      message: formatMessage({ id: 'dbi_top_charts_table_selection_limit_title' }),
                      description: formatMessage(
                        { id: 'dbi_top_charts_table_selection_limit_tips' },
                        { num: maxCheckLimit },
                      ),
                      buttons: [
                        <Button key="confirm-btn" type="primary">
                          {formatMessage({ id: 'dbi_confirm' })}
                        </Button>,
                      ],
                    });
                    return;
                  }

                  const newValue = [...value, cvmValue];
                  handleChange(newValue, context);
                  return;
                }

                //  取消勾选
                if (!payload && cvmChecked) {
                  const newValue = [...value.slice(0, cvmValueIndex), ...value.slice(cvmValueIndex + 1)];
                  handleChange(newValue, context);
                }
              }}
            />
          </div>
        </ColumnAligner>
      );
    },
  };
};

export default CheckerColumnBuilder;
